import { getPointHistory } from "@/dal/get-point-history";
import { useNavigationError } from "@/hooks/useNavigationError";
import type { PointHistory } from "@/features/common/PointHistory/type";
import axios from "axios";
import { useEffect, useRef, useState } from "react";

export const usePointHistory = () => {
  const [pointHistories, setPointHistories] = useState<PointHistory[]>([]);
  const [offset, setOffset] = useState(0);
  const [prevOffset, setPrevOffset] = useState(0);
  const bottomRef = useRef<HTMLDivElement>(null);

  const { navigateError } = useNavigationError();

  const handlePointHistoryError = (error: any) => {
    if (axios.isAxiosError(error)) {
      navigateError({
        error: error,
        endpoint: "/api/point/history",
        appName: "POINT_HISTORY",
        path: "points",
      });
    }
  };

  // 初回読み込み時
  // biome-ignore lint/correctness/useExhaustiveDependencies: need not to watch
  useEffect(() => {
    const fetchInitial = async () => {
      try {
        const { pointHistories } = await getPointHistory({ offset: 0, count: 20 });
        if (pointHistories.length === 0) {
          return;
        }
        setPointHistories(pointHistories);
        setOffset(20);
      } catch (error) {
        handlePointHistoryError(error);
      }
    };
    fetchInitial();
  }, []);

  // スクロール時
  // biome-ignore lint/correctness/useExhaustiveDependencies: need not to watch
  useEffect(() => {
    const fetchMore = async (currentOffset: number) => {
      try {
        const { pointHistories } = await getPointHistory({
          offset: currentOffset,
          count: 20,
        });
        if (pointHistories.length === 0) {
          return;
        }
        setPointHistories((prevPointHistories) => {
          const combined = [...prevPointHistories, ...pointHistories];

          // 重複を削除
          const unique = combined.reduce((acc: PointHistory[], current: PointHistory) => {
            const x = acc.find((item) => item.historyId === current.historyId);
            if (!x) {
              return acc.concat([current]);
            } else {
              return acc;
            }
          }, []);

          return unique;
        });
        setOffset((prevOffset) => prevOffset + 20);
      } catch (error) {
        handlePointHistoryError(error);
      }
    };

    // Intersection Observerのインスタンスを作成
    const observer = new IntersectionObserver(
      (entries) => {
        // 画面の一番下の要素が交差したかどうかを判定
        if (entries[0].isIntersecting) {
          // 交差したらfetchを呼び出す
          const currentOffset = offset;
          if (currentOffset - prevOffset === 20) {
            fetchMore(currentOffset);
            setPrevOffset(currentOffset);
          }
        }
      },
      {
        // 交差判定の閾値を0に設定
        threshold: 0,
      }
    );

    // 画面の一番下の要素を監視対象に登録
    if (bottomRef.current) {
      observer.observe(bottomRef.current);
    }

    // コンポーネントがアンマウントされたら監視を解除
    return () => observer.disconnect();
  }, [offset]);

  return { pointHistories, bottomRef };
};
